import { Component, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiClientService } from '../../core/api/api-client.service';
import { AuthService } from '../../core/auth/auth.service';
import {
  AssignCoursesRequest,
  CourseLessonSummary,
  CourseSummary,
  DriverDetailResponse,
  DriverStatus,
  EnrollmentStatus
} from '../../core/models/portal.models';

@Component({
  selector: 'app-driver-detail-page',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './driver-detail-page.component.html'
})
export class DriverDetailPageComponent implements OnInit {
  private readonly api = inject(ApiClientService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);

  protected readonly detail = signal<DriverDetailResponse | null>(null);
  protected readonly courses = signal<CourseSummary[]>([]);
  protected readonly notice = signal<string | null>(null);
  protected readonly canDelete = computed(() => this.auth.hasAnyRole(['SUPER_ADMIN', 'ORGANIZATION_ADMIN']));
  protected readonly assignmentDialogOpen = signal(false);
  protected readonly assignmentRetraining = signal(false);
  protected readonly selectedCourseIds = signal<number[]>([]);
  protected readonly selectedLessonIds = signal<number[]>([]);
  protected readonly assignmentDueDate = signal('');
  protected readonly assignmentReason = signal<AssignCoursesRequest['reason']>('MANUAL');
  protected readonly assignmentDescription = signal('');
  protected readonly assigning = signal(false);
  protected readonly assignmentError = signal<string | null>(null);
  private driverId = 0;

  ngOnInit(): void {
    this.driverId = Number(this.route.snapshot.paramMap.get('id'));
    this.loadDriver();
    this.api.courses().subscribe((courses) => this.courses.set(courses));
  }

  @HostListener('window:focus')
  protected reloadAfterExternalPayment(): void {
    if (this.driverId > 0) {
      this.loadDriver();
    }
  }

  private loadDriver(): void {
    this.api.driver(this.driverId).subscribe((detail) => this.detail.set(detail));
  }

  protected showNotice(message: string): void {
    this.notice.set(message);
  }

  protected openAssignmentDialog(retraining: boolean): void {
    this.assignmentRetraining.set(retraining);
    this.selectedCourseIds.set([]);
    this.selectedLessonIds.set([]);
    this.assignmentDueDate.set('');
    this.assignmentReason.set('MANUAL');
    this.assignmentDescription.set('');
    this.assignmentError.set(null);
    this.assignmentDialogOpen.set(true);
  }

  protected closeAssignmentDialog(): void {
    if (this.assigning()) {
      return;
    }
    this.assignmentDialogOpen.set(false);
    this.assignmentError.set(null);
  }

  protected toggleCourse(courseId: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedCourseIds.update((current) =>
      checked ? [...current, courseId] : current.filter((id) => id !== courseId)
    );
  }

  protected updateDueDate(event: Event): void {
    this.assignmentDueDate.set((event.target as HTMLInputElement).value);
  }

  protected updateReason(event: Event): void {
    this.assignmentReason.set((event.target as HTMLSelectElement).value as AssignCoursesRequest['reason']);
  }

  protected updateDescription(event: Event): void {
    this.assignmentDescription.set((event.target as HTMLTextAreaElement).value);
  }

  protected assignCourses(event: Event): void {
    event.preventDefault();

    if (this.assignmentRetraining() && !this.selectedLessonIds().length) {
      this.assignmentError.set('Bitte wähle mindestens eine Lektion für die Nachschulung aus.');
      return;
    }

    if (!this.assignmentRetraining() && !this.selectedCourseIds().length) {
      this.assignmentError.set('Bitte wähle mindestens eine Schulung aus.');
      return;
    }

    const selectedCourseIds = this.assignmentRetraining()
      ? this.courses()
          .filter((course) => course.lessons.some((lesson) => this.selectedLessonIds().includes(lesson.id)))
          .map((course) => course.id)
      : this.selectedCourseIds();

    const request: AssignCoursesRequest = {
      courseIds: selectedCourseIds,
      lessonIds: this.assignmentRetraining() ? this.selectedLessonIds() : null,
      dueDate: this.assignmentDueDate().trim() || null,
      retraining: this.assignmentRetraining(),
      reason: this.assignmentRetraining() ? this.assignmentReason() : null,
      description: this.assignmentDescription().trim() || null
    };

    this.assigning.set(true);
    this.assignmentError.set(null);

    this.api.assignDriverCourses(this.driverId, request).subscribe({
      next: (detail) => {
        this.detail.set(detail);
        this.assigning.set(false);
        this.assignmentDialogOpen.set(false);
        this.notice.set(this.assignmentRetraining() ? 'Nachschulung wurde zugewiesen.' : 'Schulung wurde zugewiesen.');
      },
      error: () => {
        this.assigning.set(false);
        this.assignmentError.set('Die Zuweisung konnte nicht gespeichert werden.');
      }
    });
  }

  protected deleteDriver(item: DriverDetailResponse): void {
    if (!window.confirm(`Fahrer "${item.driver.displayName}" wirklich löschen? Der Portalzugang wird ebenfalls entfernt.`)) {
      return;
    }

    this.api.deleteDriver(item.driver.id).subscribe(() => this.router.navigate(['/drivers']));
  }

  protected requestPasswordLink(item: DriverDetailResponse): void {
    this.api.requestPasswordReset({ email: item.driver.email }).subscribe({
      next: (response) => this.notice.set(response.message),
      error: () => this.notice.set('Der Passwort-Link konnte nicht erstellt werden.')
    });
  }

  protected sendInvitation(item: DriverDetailResponse): void {
    this.api.sendDriverInvitation(item.driver.id).subscribe({
      next: (response) => this.notice.set(response.message),
      error: () => this.notice.set('Die Einladung konnte nicht vorgemerkt werden.')
    });
  }

  protected sendReminder(item: DriverDetailResponse): void {
    this.api.sendDriverReminder(item.driver.id).subscribe({
      next: (response) => this.notice.set(response.message),
      error: () => this.notice.set('Die Erinnerung konnte nicht vorgemerkt werden.')
    });
  }

  protected valueOrDash(value: string | null): string {
    return value?.trim() ? value : '-';
  }

  protected toggleLesson(lesson: CourseLessonSummary, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedLessonIds.update((current) =>
      checked ? [...current, lesson.id] : current.filter((id) => id !== lesson.id)
    );
  }

  protected toggleAllLessons(course: CourseSummary, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const lessonIds = course.lessons.map((lesson) => lesson.id);
    this.selectedLessonIds.update((current) => checked
      ? [...new Set([...current, ...lessonIds])]
      : current.filter((id) => !lessonIds.includes(id))
    );
  }

  protected allLessonsSelected(course: CourseSummary): boolean {
    return course.lessons.length > 0
      && course.lessons.every((lesson) => this.selectedLessonIds().includes(lesson.id));
  }

  protected someLessonsSelected(course: CourseSummary): boolean {
    return !this.allLessonsSelected(course)
      && course.lessons.some((lesson) => this.selectedLessonIds().includes(lesson.id));
  }

  protected formatLastLogin(value: string | null): string {
    if (!value) {
      return 'Noch nie';
    }

    return new Intl.DateTimeFormat('de-DE', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(value));
  }

  protected scoreOrDash(value: number | null): string {
    return value === null ? '-' : `${value} %`;
  }

  protected statusLabel(status: DriverStatus): string {
    const labels: Record<DriverStatus, string> = {
      ACTIVE: 'Aktiv',
      INACTIVE: 'Inaktiv',
      TRAINING_IN_PROGRESS: 'In Schulung',
      READY: 'Einsatzbereit',
      RETRAINING_REQUIRED: 'Nachschulung erforderlich',
      OVERDUE: 'Überfällig',
      BLOCKED: 'Gesperrt'
    };

    return labels[status] ?? status;
  }

  protected enrollmentStatusLabel(status: EnrollmentStatus): string {
    const labels: Record<EnrollmentStatus, string> = {
      ASSIGNED: 'Zugewiesen',
      STARTED: 'Gestartet',
      IN_PROGRESS: 'In Bearbeitung',
      COMPLETED: 'Abgeschlossen',
      OVERDUE: 'Überfällig',
      FAILED: 'Nicht bestanden',
      RETRAINING_REQUIRED: 'Nachschulung erforderlich'
    };

    return labels[status] ?? status;
  }
}
